import { LEWOO_API_URL, LEWOO_TOKEN_KEY } from "../api/lewooConfig";

function getToken() {
  try {
    return localStorage.getItem(LEWOO_TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearAuth() {
  try {
    localStorage.removeItem(LEWOO_TOKEN_KEY);
    localStorage.removeItem("lewoo_user");
  } catch {}
}

async function request(method, path, body = null) {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const url = `${LEWOO_API_URL}${path}`;
  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);

  const response = await fetch(url, opts);
  const data = await response.json().catch(() => null);

  if (response.status === 401) {
    const isLogin = path.includes("/api/auth/login");
    if (!isLogin) {
      clearAuth();
      if (window.location.pathname !== "/admin/login") {
        window.location.href = "/admin/login";
      }
    }
    throw new Error(data?.error?.message || "Session expirée");
  }

  if (!response.ok) {
    const msg = data?.error?.message || `Erreur ${response.status}`;
    throw new Error(msg);
  }

  return data;
}

export function get(path) {
  return request("GET", path);
}

export function post(path, body) {
  return request("POST", path, body);
}

export function put(path, body) {
  return request("PUT", path, body);
}

export function del(path) {
  return request("DELETE", path);
}

export function patch(path, body) {
  return request("PATCH", path, body);
}

export async function upload(path, file, extra = {}) {
  const token = getToken();
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const fd = new FormData();
  fd.append("file", file);
  Object.entries(extra).forEach(([k, v]) => fd.append(k, v));

  const response = await fetch(`${LEWOO_API_URL}${path}`, { method: "POST", headers, body: fd });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const msg = data?.error?.message || `Erreur ${response.status}`;
    throw new Error(msg);
  }

  return data;
}

export async function download(path, filename) {
  const token = getToken();
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${LEWOO_API_URL}${path}`, { method: "GET", headers });
  if (!response.ok) {
    // Le backend peut renvoyer JSON même sur un endpoint blob
    let msg = `Erreur ${response.status}`;
    try {
      const ct = response.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const j = await response.json();
        msg = j?.error?.message || j?.message || msg;
      } else {
        // blob JSON masqué (cas xlsx qui renvoie erreur JSON avec content-type octet-stream)
        const txt = await response.text();
        try {
          const j = JSON.parse(txt);
          msg = j?.error?.message || j?.message || txt.slice(0, 200) || msg;
        } catch {
          msg = txt.slice(0, 200) || msg;
        }
      }
    } catch {}
    throw new Error(msg);
  }

  // Détection contenu réel: si le serveur a renvoyé du JSON au lieu du binaire attendu (ex: xlsx corrompu)
  const contentType = response.headers.get("content-type") || "";
  const blob = await response.blob();
  if (contentType.includes("application/json") || blob.type.includes("application/json")) {
    let txt = "";
    try { txt = await blob.text(); } catch {}
    try {
      const j = JSON.parse(txt);
      throw new Error(j?.error?.message || j?.message || `Réponse JSON inattendue pour ${filename}`);
    } catch (e) {
      if (e.message && !e.message.includes("Unexpected token")) throw e;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return blob;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export { getToken, clearAuth };
export default { get, post, put, del, patch, download, upload, getToken, clearAuth };
